import { useTranslationApp } from "../../../../lib/i18next";
import TextField from "../../../Form/TextField";
import {
  ADS_CAMPAIGN_OBJECTIVE_AWARENESS_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_TRAFFIC_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_INSTAGRAM_PROFILE_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_VIDEO_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_LEAD_INSTANT_FORM_OPTION,
  metaCurrenciesWithDecimals,
  ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_MESSAGE_TEMPLATE,
  ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION,
  META,
  ADS_CAMPAIGN_META_OPTIMIZATION_GOAL_REACH,
  ADS_CAMPAIGN_META_OPTIMIZATION_GOAL_THRUPLAY,
  ADS_CAMPAIGN_META_OPTIMIZATION_GOAL_LINK_CLICKS,
  ADS_CAMPAIGN_META_OPTIMIZATION_GOAL_LANDING_PAGE_VIEWS,
} from "../../../../utils/constants";
import {
  TARGET_CPA_PLACEHOLDER,
  BID_CONTROL_PLACEHOLDER,
  IN_CURRENCY,
  BID_GOAL_EXPLANATION,
  TARGET_CPA_EXPLANATION,
  OPTIMIZATION_GOAL_CONVERSATIONS,
  OPTIMIZATION_GOAL_VISIT_INSTAGRAM_PROFILE,
  OPTIMIZATION_GOAL_LINK_CLICKS,
  OPTIMIZATION_GOAL_THRUPLAY,
  OPTIMIZATION_GOAL_LANDING_PAGE_VIEWS,
  OPTIMIZATION_GOAL_POST_ENGAGEMENT,
  OPTIMIZATION_GOAL_OFFSITE_CONVERSIONS,
  OPTIMIZATION_GOAL_LEAD_GENERATION,
  OPTIMIZATION_GOAL_APP_INSTALLS,
  OPTIMIZATION_GOAL_CPM,
} from "../../../../i18n/keysTranslations";
import NumberDecimalField from "../../../Form/NumberDecimalField";
import NumberField from "../../../Form/NumberField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ChipOptional from "../../../Chip/ChipOptional";
import { getReactHookFormNestedError } from "../../../../utils/errors";

const getSelectedOptimizationGoalLabelKey = (optimizationGoal) => {
  switch (optimizationGoal) {
    case ADS_CAMPAIGN_META_OPTIMIZATION_GOAL_REACH:
      return OPTIMIZATION_GOAL_CPM;
    case ADS_CAMPAIGN_META_OPTIMIZATION_GOAL_THRUPLAY:
      return OPTIMIZATION_GOAL_THRUPLAY;
    case ADS_CAMPAIGN_META_OPTIMIZATION_GOAL_LINK_CLICKS:
      return OPTIMIZATION_GOAL_LINK_CLICKS;
    case ADS_CAMPAIGN_META_OPTIMIZATION_GOAL_LANDING_PAGE_VIEWS:
      return OPTIMIZATION_GOAL_LANDING_PAGE_VIEWS;
    default:
      return null;
  }
};

const BidGoalMeta = ({
  control,
  name,
  errors,
  objective,
  destinationType,
  optimizationGoal,
  disabled = false,
  currency,
  labelPosition = "separate",
  showOptionalChip = true,
}) => {
  const { t } = useTranslationApp();

  const error = getReactHookFormNestedError(errors, name);

  const getOptimizationGoalCampaignMeta = () => {
    const selectedLabelKey =
      getSelectedOptimizationGoalLabelKey(optimizationGoal);
    if (selectedLabelKey) {
      return t(selectedLabelKey);
    }

    switch (objective) {
      case ADS_CAMPAIGN_OBJECTIVE_AWARENESS_OPTION:
        return t(OPTIMIZATION_GOAL_CPM);

      case ADS_CAMPAIGN_OBJECTIVE_TRAFFIC_OPTION:
        if (
          ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_MESSAGE_TEMPLATE.includes(
            destinationType
          )
        )
          return t(OPTIMIZATION_GOAL_CONVERSATIONS);

        if (
          destinationType ===
          ADS_CAMPAIGN_META_DESTINATION_TYPE_INSTAGRAM_PROFILE_OPTION
        )
          return t(OPTIMIZATION_GOAL_VISIT_INSTAGRAM_PROFILE);

        return t(OPTIMIZATION_GOAL_LINK_CLICKS);

      case ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION:
        if (
          ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_MESSAGE_TEMPLATE.includes(
            destinationType
          )
        )
          return t(OPTIMIZATION_GOAL_CONVERSATIONS);

        if (
          destinationType ===
          ADS_CAMPAIGN_META_DESTINATION_TYPE_INSTAGRAM_PROFILE_OPTION
        )
          return t(OPTIMIZATION_GOAL_VISIT_INSTAGRAM_PROFILE);

        if (
          destinationType === ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_VIDEO_OPTION
        )
          return t(OPTIMIZATION_GOAL_THRUPLAY);
        if (
          destinationType === ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION
        )
          return t(OPTIMIZATION_GOAL_POST_ENGAGEMENT);
        return t(OPTIMIZATION_GOAL_OFFSITE_CONVERSIONS);

      case ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION:
        if (
          ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_MESSAGE_TEMPLATE.includes(
            destinationType
          )
        )
          return t(OPTIMIZATION_GOAL_CONVERSATIONS);

        if (
          ADS_CAMPAIGN_META_DESTINATION_TYPE_LEAD_INSTANT_FORM_OPTION ===
          destinationType
        )
          return t(OPTIMIZATION_GOAL_LEAD_GENERATION);

        return t(OPTIMIZATION_GOAL_OFFSITE_CONVERSIONS);

      case ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION:
        return t(OPTIMIZATION_GOAL_APP_INSTALLS);

      case ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION:
        if (
          ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_MESSAGE_TEMPLATE.includes(
            destinationType
          )
        )
          return t(OPTIMIZATION_GOAL_CONVERSATIONS);

        return t(OPTIMIZATION_GOAL_OFFSITE_CONVERSIONS);
    }
  };

  const getBidGoalLabel = () => {
    const isMessageOrInstagramProfile =
      ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_MESSAGE_TEMPLATE.includes(
        destinationType
      ) ||
      destinationType ===
        ADS_CAMPAIGN_META_DESTINATION_TYPE_INSTAGRAM_PROFILE_OPTION;

    switch (objective) {
      case ADS_CAMPAIGN_OBJECTIVE_AWARENESS_OPTION:
        return t(TARGET_CPA_PLACEHOLDER);

      case ADS_CAMPAIGN_OBJECTIVE_TRAFFIC_OPTION:
        if (
          destinationType === ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION
        ) {
          return t(TARGET_CPA_PLACEHOLDER);
        }
        if (isMessageOrInstagramProfile) {
          return t(BID_CONTROL_PLACEHOLDER);
        }
        break;

      case ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION:
        if (
          destinationType ===
            ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION ||
          destinationType ===
            ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_VIDEO_OPTION ||
          destinationType === ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION
        ) {
          return t(TARGET_CPA_PLACEHOLDER);
        }
        if (isMessageOrInstagramProfile) {
          return t(BID_CONTROL_PLACEHOLDER);
        }
        break;

      case ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION:
        return t(TARGET_CPA_PLACEHOLDER);

      case ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION:
        if (
          destinationType ===
            ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION ||
          destinationType ===
            ADS_CAMPAIGN_META_DESTINATION_TYPE_LEAD_INSTANT_FORM_OPTION
        ) {
          return t(TARGET_CPA_PLACEHOLDER);
        }
        if (
          ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_MESSAGE_TEMPLATE.includes(
            destinationType
          )
        ) {
          return t(BID_CONTROL_PLACEHOLDER);
        }
        break;

      case ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION:
        return t(TARGET_CPA_PLACEHOLDER);

      default:
        return t(TARGET_CPA_PLACEHOLDER);
    }
  };

  const label = getBidGoalLabel();
  const optimizationGoalLabel = getOptimizationGoalCampaignMeta();
  const isTargetCpa = label === t(TARGET_CPA_PLACEHOLDER);
  const finalLabel = `${label} ${t(IN_CURRENCY, { currency })}`;

  return (
    <>
      {labelPosition === "separate" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="body1">{finalLabel}</Typography>
          {showOptionalChip ? <ChipOptional size="small" /> : null}
        </Box>
      )}
      <TextField
        control={control}
        name={name}
        errors={errors}
        variant="filled"
        fullWidth
        label={labelPosition === "separate" ? "" : finalLabel}
        disabled={disabled}
        InputProps={{
          inputComponent: metaCurrenciesWithDecimals.includes(currency)
            ? NumberDecimalField
            : NumberField,
        }}
      />
      {!error?.message && (
        <Typography variant="caption" color="fields.placeholder">
          {`${t(isTargetCpa ? TARGET_CPA_EXPLANATION : BID_GOAL_EXPLANATION, {
            platform: t(META),
          })} ${optimizationGoalLabel}`}
        </Typography>
      )}
    </>
  );
};

export default BidGoalMeta;
